import { useLayout, usePinch, useSpring } from '#imports';
import { Quaternion, Vector2, Vector3 } from 'three';
import { computed, ref, watch } from 'vue';

import { clamp, lerp, map, randomFloat, toRadians } from '@resn/gozer-math';
import { useRef, useWindowPointer } from '@resn/gozer-vue';
import { gsap } from '@resn/gsap';

import { useInteractionDebug } from '~/composables/useInteractionDebug';
import { useSwipeAdvance } from '~/composables/useSwipeAdvance';
import { useAudio } from '~/providers/AudioProvider';

const createQuaternionMotion = () => {
    return {
        pos: new Vector3(),
        posTarget: new Vector3(),
        rot: new Vector3(),
        rotTarget: new Vector3(),
        xAxis: new Vector3(1, 0, 0),
        yAxis: new Vector3(0, 1, 0),
        zAxis: new Vector3(0, 0, 1),
        x: new Quaternion(),
        y: new Quaternion(),
        z: new Quaternion(),
        final: new Quaternion(),
        base: new Quaternion(),
    };
};

export const useInteractionMotion = ({
    maxZoom = 0.5,
    debugLabel = 'interaction',
    zoomEnabled = ref(true),
} = {}) => {
    const debugOwner = Symbol(debugLabel);
    const interactionDebug = useInteractionDebug();
    const tweens = { interactivePr: 0 };

    const pointerPrev = new Vector2();
    const pointerDelta = new Vector2();
    const startPointerPos = new Vector2();
    const pressMotion = new Vector3();

    const motion = createQuaternionMotion();
    const { isMobileLayout } = useLayout();

    const rotSpring = useSpring(motion.rot, { stiffness: 200, damping: 25 });
    const zoomSpring = useSpring(motion.pos, { stiffness: 200, damping: 25 });
    const threshold = computed(() => (isMobileLayout.value ? 170 : 170));

    const isPressing = ref(false);
    const isZooming = ref(false);
    const rotSnapValue = ref(0);
    const isFlipped = ref(false);
    const active = ref(false);
    const zoomPr = ref(0);

    const audio = useAudio();
    const windowRef = useRef('window');
    const swipeAdvance = useSwipeAdvance({ offsetRange: threshold.value });

    const syncIsFlipped = () => {
        const steps = Math.round(rotSnapValue.value / Math.PI);
        isFlipped.value = Math.abs(steps) % 2 === 1;
    };

    const enable = () => {
        active.value = true;
        interactionDebug.setInteractionActive(debugOwner, debugLabel, true);
    };

    const disable = () => {
        active.value = false;
        interactionDebug.setInteractionActive(debugOwner, debugLabel, false);
    };

    const pinch = usePinch(windowRef, {
        enableOnMount: false,
        onPinchStart: () => (isPressing.value = false),
        onPinchMove: ({ zoomDelta }) => {
            const { posTarget } = motion;
            posTarget.z = clamp(posTarget.z + zoomDelta * 0.005, 0, maxZoom);
            zoomSpring.set({ z: posTarget.z });
        },
    });

    const onPointerMove = ({ x, y, fromDown }) => {
        if (!active.value) return;
        if (pinch.isPinching.value) return;

        if (fromDown) {
            pointerPrev.set(x, y);
            pointerDelta.set(0, 0);
            startPointerPos.set(x, y);
            swipeAdvance.resetPointerTracking(x, y);
            return;
        }

        const { rotTarget } = motion;

        if (isDown.value) {
            swipeAdvance.recordPointerSample(x, y);
            const deltaxFromStart = x - startPointerPos.x;
            const deltayFromStart = y - startPointerPos.y;

            const dir = Math.sign(deltaxFromStart);
            const distx = Math.abs(deltaxFromStart);

            // if (distx > threshold.value) {
            //     rotSnapValue.value += dir * Math.PI;
            //     startPointerPos.set(x, y);
            // }

            rotTarget.x = lerp(deltayFromStart / threshold.value, 0, toRadians(20));
            rotTarget.y =
                lerp(deltaxFromStart / threshold.value, 0, toRadians(80)) + rotSnapValue.value;

            rotSpring.set({ x: rotTarget.x, y: rotTarget.y });
        }

        pointerDelta.set(x - pointerPrev.x, y - pointerPrev.y);
        pointerPrev.set(x, y);
    };

    const onPointerDown = (val) => {
        if (!active.value) return;
        isPressing.value = val;

        if (val) {
            swipeAdvance.clearPointerSamples();
            syncRotSnapFromMotion();

            rotSpring.state.config.stiffness = 200;
            rotSpring.state.config.damping = 25;
        } else {
            // const deltaxFromStart = pointerPrev.x - startPointerPos.x;
            // const dir = Math.sign(deltaxFromStart);
            // const distx = Math.abs(deltaxFromStart);
            // if (distx > threshold.value) {
            //     rotSnapValue.value += dir * Math.PI;
            // }

            const { shouldAdvance, sign } = swipeAdvance.getShouldAdvance('x');

            const pitch = randomFloat(0.8, 1.15);

            if (shouldAdvance) {
                rotSnapValue.value += sign.x * Math.PI;
                audio.play('swoosh', { pitch });
            } else {
                const closest = Math.round(motion.rot.y / Math.PI) * Math.PI;
                if (rotSnapValue.value !== closest) {
                    audio.play('swoosh', { pitch });
                }
                rotSnapValue.value = closest;
            }

            const { rotTarget } = motion;
            rotTarget.set(0, 0, 0);
            rotSpring.set({ x: 0, y: rotSnapValue.value });

            rotSpring.state.config.stiffness = 280;
            rotSpring.state.config.damping = 18;
            syncIsFlipped();
        }
    };

    const { isDown } = useWindowPointer(onPointerMove, onPointerDown);

    const pinchingOrGrabbing = computed(() => {
        return pinch.isPinching.value || isDown.value;
    });

    const setPressing = (val) => {
        gsap.killTweensOf(pressMotion);
        gsap.to(pressMotion, {
            z: val ? -0.2 : 0,
            duration: val ? 0.2 : 0.4,
            ease: val ? 'power3.out' : 'back.out(3)',
        });
    };

    const onWheel = (event) => {
        const delta = event.deltaY;
        const { posTarget } = motion;
        posTarget.z = clamp(posTarget.z + delta * 0.002, 0, maxZoom);
        zoomSpring.set({ z: posTarget.z });

        return false;
    };

    const update = () => {
        const { x, y, z } = motion.rot;
        motion.x.setFromAxisAngle(motion.xAxis, x);
        motion.y.setFromAxisAngle(motion.yAxis, y);
        motion.z.setFromAxisAngle(motion.zAxis, z);

        motion.final.copy(motion.x).multiply(motion.y).multiply(motion.z);

        motion.final.slerp(motion.base, 1 - tweens.interactivePr);

        isZooming.value = motion.pos.z > maxZoom * 0.1;
        zoomPr.value = map(motion.pos.z, 0, maxZoom, 0, 1, true);

        interactionDebug.recordInteraction(debugOwner, {
            rotationCurrent: motion.rot,
            rotationTarget: motion.rotTarget,
        });
    };

    let zoomListenersOn = false;
    const applyZoomListeners = (val) => {
        if (val === zoomListenersOn) return;
        zoomListenersOn = val;

        if (val) {
            pinch.enable();
            addEventListener('wheel', onWheel);
        } else {
            pinch.disable();
            removeEventListener('wheel', onWheel);
        }
    };

    const setActive = (val) => {
        const interactivePr = val ? 1 : 0;
        const duration = val ? 0 : 1;

        gsap.killTweensOf(tweens, 'interactivePr');
        if (duration === 0) tweens.interactivePr = interactivePr;
        else gsap.to(tweens, { interactivePr, duration, ease: 'power1.inOut' });

        if (val) {
            syncRotSnapFromMotion();
            applyZoomListeners(zoomEnabled.value);
        } else {
            applyZoomListeners(false);

            isPressing.value = false;
            zoomSpring.set({ z: 0 });

            reset(true);
        }
    };

    watch(zoomEnabled, (val) => {
        if (!active.value) return;

        applyZoomListeners(val);
        if (!val) zoomSpring.set({ z: 0 });
    });

    const setZoom = (val = false, force = false) => {
        const zoom = val ? maxZoom : 0;
        const { posTarget } = motion;
        posTarget.z = zoom;
        zoomSpring.set({ z: posTarget.z }, force);
    };

    const setZoomManual = (val = 0) => {
        const { posTarget } = motion;
        posTarget.z = clamp(val, 0, maxZoom);
        zoomSpring.set({ z: posTarget.z });
    };

    const syncRotSnapFromMotion = () => {
        const angle = Math.PI;
        const snappedY = Math.round(motion.rot.y / angle) * angle;

        rotSnapValue.value = snappedY;
        motion.rotTarget.set(0, snappedY, 0);
        rotSpring.set({ x: 0, y: snappedY });
        syncIsFlipped();
    };

    const reset = (fullReset = false) => {
        const { rotTarget, rot } = motion;
        const angle = fullReset ? Math.PI * 2 : Math.PI;

        rotTarget.x = Math.round(rot.x / angle) * angle;
        rotTarget.y = Math.round(rot.y / angle) * angle;

        rotSnapValue.value = fullReset ? 0 : rotTarget.y;
        rotSpring.set({ x: rotTarget.x, y: rotTarget.y });
        zoomSpring.set({ z: 0 });
        syncIsFlipped();
    };

    watch(active, setActive);
    watch(isPressing, setPressing);

    return {
        motion,
        pressMotion,
        isZooming,
        isFlipped,
        pinchingOrGrabbing,
        update,
        setZoom,
        setZoomManual,
        zoomPr,
        reset,
        enable,
        disable,
    };
};
